import React, {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {useGoogleLogin} from '@react-oauth/google';
import AppleLogin from 'react-apple-login';
import axios from 'axios';
import styles from './LoginEmailPage.module.css';
import logoSvg from './tables/static_files/kiraAi logo.svg';
import {ENDPOINT_BASE_URL} from './configs/config';

// Utility function to parse JWT token
const parseJwt = (token) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));
  return JSON.parse(jsonPayload);
};

const LoginEmailPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [isVerification, setIsVerification] = useState(false);
  const [isResend, setIsResend] = useState(false);
  const [seconds, setSeconds] = useState(30);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyData, setVerifyData] = useState(null);
  const emailInputRef = useRef(null);
  const codeInputRef = useRef(null);
  const navigate = useNavigate();
  let timer;

  // Timer for resend functionality
  const time = () => {
    if (isVerification || isResend) {
      timer = setTimeout(() => {
        const newSeconds = seconds - 1;
        if (newSeconds === 0) {
          setIsResend(false);
          clearTimeout(timer);
        } else {
          setSeconds(newSeconds);
        }
      }, 1000);
    }
  };

  useEffect(() => {
    time();
    return () => clearTimeout(timer);
  }, [seconds, isVerification, isResend]);

  const sendEmail = () => {
    axios.post(`${ENDPOINT_BASE_URL}/auth/send-verification-code`, {email: email})
      .then((res) => {
        setVerifyData(res.data);
      }).catch((error) => {
        console.info('sendEmailError', error);
        setError('Failed to send code. Please try again.');
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const emailReg = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) {
      if (emailInputRef.current) emailInputRef.current.focus();
      setError("Email is required");
      return;
    }
    if (!emailReg.test(email)) {
      if (emailInputRef.current) emailInputRef.current.focus();
      setError("Please enter your valid email");
      return;
    }
    sendEmail();
    setError("");
    setIsVerification(true);
  };

  const handleCodeChange = (code) => {
    setVerifyCode(code);
    if (code.length === 6) {
      axios.post(`${ENDPOINT_BASE_URL}/auth/verify-code`, {email: email, code})
        .then((res) => {
          setVerifyData(res.data);
          if (res.data.success) {
            onSaveInfo({email: email, type: '0'});
          }
        })
        .catch((error) => {
          console.info('error verify code', error);
          setVerifyData({success: false, msg: "The verification code is invalid."});
        });
    }
  };

  const onSaveInfo = ({email, username, type}) => {
    setIsSubmitting(true);
    axios.post(
      `${ENDPOINT_BASE_URL}/auth/save`,
      {email, type},
      {
        validateStatus: (status) => {
          return (status >= 200 && status < 300) || status === 409;
        }
      }
    )
      .then((res) => {
        setIsSubmitting(false);

        if (res && res.data && res.data.data && res.data.data.hash_code) {
          const hashCode = res.data.data.hash_code;
          localStorage.setItem('gatewayToken', hashCode);
          const isProduction = window.location.hostname === 'app.cutwaterglobal.com';
          if (isProduction) {
            window.location.href = `https://app.cutwaterglobal.com/swap?token=${encodeURIComponent(hashCode)}`;
          } else {
            window.location.href = `/swap?token=${encodeURIComponent(hashCode)}`;
          }
        } else {
          setError('No token received from server. Please try again.');
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        setError('Failed to complete sign in. Please try again.');
        console.info('error', error);
      });
  };

  const onGoogleLogin = useGoogleLogin({
    scope: "email profile",
    // Don't specify redirect_uri - let it use the default popup flow
    // The redirect URI needs to be added to Google Cloud Console for your domain
    onSuccess: async (codeResponse) => {
      console.log("Google login successful. Code response:", codeResponse);

      const token = codeResponse.access_token;
      const userInfo = await axios.get(
        "https://www.googleapis.com/oauth2/v3/userinfo",
        {headers: {Authorization: `Bearer ${token}`}}
      );
      const result = userInfo.data;
      if (result.email_verified) {
        const {email, name} = result;
        console.log("got name and email from google login", email, name);
        onSaveInfo({email, username: name, type: '1'});
      }
    },
    onError: (errorResponse) => {
      console.error('Google login error:', errorResponse);
      setError('Google sign in failed. Please try again.');
      setIsSubmitting(false);
    },
  });

  return (
    <div className={styles.page}>
      <div className={styles.leftPane}>
        <div className={styles.leftInner}>
          <img className={styles.logo} src={logoSvg} alt="Cutwater" />
          <p className={styles.tagline}>
            Earn passive income automatically
            <br />
            with USDI stablecoin.
          </p>
        </div>
      </div>
      <div className={styles.rightPane}>
        <div className={styles.card}>
          {!isVerification && (
            <>
              <div className={styles.cardHeader}>
                <h1>Welcome to Cutwater</h1>
                <p>Sign in to start earning multi-asset yields</p>
              </div>
              <form className={styles.form} onSubmit={handleSubmit}>
            <label className={styles.label} htmlFor="email">
              Email Address
            </label>
            <div className={styles.inputWrap}>
              <svg
                className={styles.mailIcon}
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <path
                  d="m4 8 8 6 8-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                id="email"
                ref={emailInputRef}
                type="email"
                name="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <span className={styles.helper}>
              We'll send you a one-time passcode to sign in
            </span>
            <button className={styles.primary} type="submit" disabled={!email || isSubmitting}>
              Continue with Email
            </button>
            {status && <span className={styles.status}>{status}</span>}
            {error && <span className={styles.error}>{error}</span>}
            <div className={styles.divider}>
              <span>OR</span>
            </div>
            <button 
              type="button" 
              className={styles.secondary}
              onClick={() => onGoogleLogin()}
              disabled={isSubmitting}
            >
              <svg className={styles.googleIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  d="M23.49 12.27c0-.81-.07-1.61-.21-2.39H12v4.51h6.48a5.55 5.55 0 0 1-2.4 3.65v3.01h3.88c2.27-2.09 3.53-5.17 3.53-8.78Z"
                  fill="#4285F4"
                />
                <path
                  d="M12 24c3.24 0 5.95-1.07 7.94-2.9l-3.88-3.01c-1.08.73-2.46 1.16-4.06 1.16-3.13 0-5.79-2.11-6.74-4.95H1.25v3.11A12 12 0 0 0 12 24Z"
                  fill="#34A853"
                />
                <path
                  d="M5.26 14.3a7.2 7.2 0 0 1 0-4.6V6.59H1.25a12 12 0 0 0 0 10.82l4.01-3.11Z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.94 1.07 15.23 0 12 0A12 12 0 0 0 1.25 6.59l4.01 3.11C6.21 6.86 8.87 4.75 12 4.75Z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </button>
            <AppleLogin
              clientId="trading.kira.app"
              redirectURI="https://gateway.kira.trading/"
              scope="email name"
              usePopup={true}
              responseType="code"
              responseMode="query"
              callback={(userInfo) => {
                console.log('Apple login callback', userInfo);
                setIsSubmitting(false);
                if (userInfo && userInfo?.authorization?.id_token) {
                  try {
                    const { email } = parseJwt(userInfo?.authorization?.id_token);
                    
                    if (userInfo?.user) {
                      onSaveInfo({ 
                        email, 
                        username: userInfo.user.name.firstName + ' ' + userInfo.user.name.lastName, 
                        type: '2' 
                      });
                      return;
                    }
                    onSaveInfo({ email, type: '2' });
                  } catch (error) {
                    console.error('Error parsing Apple token:', error);
                    setError('Failed to process Apple sign in. Please try again.');
                  }
                } else if (userInfo && userInfo.error) {
                  console.error('Apple login error:', userInfo.error);
                  setError('Apple sign in failed: ' + (userInfo.error_description || userInfo.error));
                } else {
                  console.error('Apple login error - no userInfo or id_token:', userInfo);
                  setError('Apple sign in failed. Please try again.');
                }
              }}
              render={(renderProps) => (
                <button 
                  type="button" 
                  className={styles.secondary}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsSubmitting(true);
                    setError('');
                    renderProps.onClick(e);
                  }}
                  disabled={isSubmitting}
                >
                  <span className={styles.appleIcon} aria-hidden="true"></span>
                  Continue with Apple
                </button>
              )}
              />
              </form>
            </>
          )}

          {isVerification && (
            <>
              <button
                type="button"
                onClick={() => {
                  setEmail("");
                  setVerifyCode('');
                  setVerifyData(null);
                  setIsVerification(false);
                  setSeconds(30);
                  setError('');
                }}
                className={styles.backButton}
                style={{background: 'none', border: 'none', color: '#5BA4E6', cursor: 'pointer', marginBottom: '20px', fontSize: '14px'}}
              >
                ← Back
              </button>
              <div className={styles.cardHeader}>
                <h1>Enter verification code</h1>
                <p>We sent a 6-digit code to {email}</p>
              </div>
              <div className={styles.form}>
                <div 
                  className={styles.codeInputContainer}
                  onClick={() => codeInputRef.current?.focus()}
                >
                  <input
                    ref={codeInputRef}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={verifyCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^\d]/g, "").slice(0, 6);
                      handleCodeChange(val);
                    }}
                    className={styles.codeInputHidden}
                    autoFocus
                  />
                  {Array.from({length: 6}, (_, index) => (
                    <div
                      key={index}
                      className={`${styles.codeInputBox} ${verifyData && !verifyData.success ? styles.error : ''}`}
                    >
                      {verifyCode[index] || ''}
                    </div>
                  ))}
                </div>
                {verifyData && !verifyData.success && (
                  <span className={styles.error}>{verifyData.msg || 'Invalid code'}</span>
                )}
                <div className={styles.codeActions}>
                  <button
                    type="button"
                    className={styles.link}
                    onClick={() => {
                      if (seconds > 1) return;
                      setIsResend(true);
                      setSeconds(30);
                      sendEmail();
                      setVerifyCode('');
                      setVerifyData(null);
                    }}
                    disabled={seconds > 1}
                  >
                    Resend code
                  </button>
                  <span className={styles.dividerText}>•</span>
                  <button
                    type="button"
                    className={styles.link}
                    onClick={() => {
                      setEmail("");
                      setVerifyCode('');
                      setIsVerification(false);
                      setSeconds(30);
                      setVerifyData(null);
                      setError('');
                    }}
                  >
                    Change email
                  </button>
                </div>
                {seconds > 0 && seconds < 30 && (
                  <div className={styles.status} style={{textAlign: 'center', marginTop: '8px'}}>
                    Please wait {seconds} seconds before sending another email
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginEmailPage;
